/**
 * Signal Alpha Lab — empirical predictive-power analysis of the platform's
 * trading signals under dual benchmarks (vs S&P 500, vs own sector via
 * precomputed alpha), plus options-market conditioning and white paper output.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Activity, Building2, FileText, FlaskConical, Gem, GitBranch, Layers, Network, Target, TrendingUp,
} from "lucide-react";

import { apiGet, useSignalJob } from "../components/stats/statsApi";
import { PanelSummary, RunBar, AlphaVal, SigBadge } from "../components/stats/common";
import { ContinuousSignalSection } from "../components/stats/ContinuousSignalSection";
import { ClusterSection } from "../components/stats/ClusterSection";
import { OptionsOverlayTab } from "../components/stats/OptionsOverlayTab";
import { WhitePaperTab } from "../components/stats/WhitePaperTab";
import { CapBucketBlock, CAP_LABELS } from "../components/stats/CapBucketBlock";
import { SuperSignalTab } from "../components/stats/SuperSignalTab";
import { BigCapLabTab } from "../components/stats/BigCapLabTab";
import { RegimeLabTab } from "../components/stats/RegimeLabTab";
import { HiddenGemsTab } from "../components/stats/HiddenGemsTab";

const SignalCard = ({ title, description, signal, children, testId }) => (
  <Card data-testid={testId}>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent className="space-y-5">
      <RunBar
        result={signal.result}
        job={signal.job}
        running={signal.running}
        onRun={signal.run}
        testId={`${testId}-runbar`}
      />
      {signal.result?.result && (
        <>
          <PanelSummary
            panel={signal.result.result.panel}
            rebalance={signal.result.result.rebalance}
          />
          {children(signal.result.result)}
        </>
      )}
    </CardContent>
  </Card>
);

const SubSignalBlock = ({ name, block, horizons, capData }) => (
  <div className="space-y-3 pt-2">
    <div className="flex items-center gap-2">
      <h3 className="text-sm font-semibold">{block.label}</h3>
      <div className="h-px flex-1 bg-border" />
    </div>
    <ContinuousSignalSection block={block} horizons={horizons} testIdPrefix={name} />
    <CapBucketBlock capData={capData} testIdPrefix={name} />
  </div>
);

export default function StatsPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("centrality");
  const [summary, setSummary] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await apiGet("/stats/summary", token));
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchSummary();
  }, [token, fetchSummary]);

  const centrality = useSignalJob("centrality", token, { onComplete: fetchSummary });
  const targets = useSignalJob("target_prices", token, { onComplete: fetchSummary });
  const nowcasting = useSignalJob("nowcasting", token, { onComplete: fetchSummary });
  const overlay = useSignalJob("options_overlay", token, { onComplete: fetchSummary });
  const superSignal = useSignalJob("super_signal", token, { onComplete: fetchSummary });
  const bigCapLab = useSignalJob("big_cap_lab", token, { onComplete: fetchSummary });
  const regimeLab = useSignalJob("regime_refinement", token, { onComplete: fetchSummary });
  const hiddenGems = useSignalJob("hidden_gems", token, { onComplete: fetchSummary });

  const cov = summary?.data_coverage;

  return (
    <div className="space-y-6" data-testid="stats-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <FlaskConical className="w-7 h-7 text-primary" />
            Signal Alpha Lab
          </h1>
          <div className="text-muted-foreground mt-1">
            Do our signals generate alpha? Quantile sorts, ICs and Newey-West t-stats under two
            benchmarks: <Badge variant="outline" className="mx-1">vs S&P 500</Badge> and
            <Badge variant="outline" className="mx-1">vs own sector</Badge> (precomputed
            sector × cap-bucket alpha).
          </div>
        </div>
      </div>

      {/* Coverage strip */}
      {cov && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="coverage-cards">
          {[
            ["Centrality", cov.centrality, Network],
            ["Dynamic Upside", cov.dynamic_upside, Target],
            ["Nowcasting", cov.nowcasting, Activity],
          ].map(([label, c, Icon]) => (
            <Card key={label}>
              <CardContent className="pt-4 pb-3 flex items-center gap-3">
                <Icon className="w-5 h-5 text-muted-foreground" />
                <div>
                  <div className="text-sm font-semibold">
                    {c?.total_records?.toLocaleString()} <span className="font-normal text-muted-foreground">records</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {label} · {c?.date_range?.start} → {c?.date_range?.end}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="centrality" data-testid="tab-centrality" className="flex items-center gap-2">
            <Network className="w-4 h-4" /><span className="hidden sm:inline">Centrality</span>
          </TabsTrigger>
          <TabsTrigger value="targets" data-testid="tab-targets" className="flex items-center gap-2">
            <Target className="w-4 h-4" /><span className="hidden sm:inline">Targets</span>
          </TabsTrigger>
          <TabsTrigger value="nowcasting" data-testid="tab-nowcasting" className="flex items-center gap-2">
            <Activity className="w-4 h-4" /><span className="hidden sm:inline">Nowcasting</span>
          </TabsTrigger>
          <TabsTrigger value="options" data-testid="tab-options" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /><span className="hidden sm:inline">Options</span>
          </TabsTrigger>
          <TabsTrigger value="supersignal" data-testid="tab-supersignal" className="flex items-center gap-2">
            <Layers className="w-4 h-4" /><span className="hidden sm:inline">Super Signal</span>
          </TabsTrigger>
          <TabsTrigger value="bigcap" data-testid="tab-bigcap" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" /><span className="hidden sm:inline">Big Cap</span>
          </TabsTrigger>
          <TabsTrigger value="regimelab" data-testid="tab-regimelab" className="flex items-center gap-2">
            <GitBranch className="w-4 h-4" /><span className="hidden sm:inline">Regime Lab</span>
          </TabsTrigger>
          <TabsTrigger value="gems" data-testid="tab-gems" className="flex items-center gap-2">
            <Gem className="w-4 h-4" /><span className="hidden sm:inline">Hidden Gems</span>
          </TabsTrigger>
          <TabsTrigger value="whitepaper" data-testid="tab-whitepaper" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /><span className="hidden sm:inline">White Paper</span>
          </TabsTrigger>
        </TabsList>

        {/* Centrality */}
        <TabsContent value="centrality" className="space-y-4">
          <SignalCard
            testId="centrality-card"
            title="Ownership Centrality → Forward Alpha"
            description="Does a stock's position in the 13F holdings graph predict future alpha? Quarterly observation dates, global and sector centrality."
            signal={centrality}
          >
            {(res) => (
              <>
                {Object.entries(res.signals || {}).map(([name, block]) => (
                  <SubSignalBlock key={name} name={name} block={block} horizons={res.horizons}
                    capData={res.by_cap_bucket?.[name]} />
                ))}
              </>
            )}
          </SignalCard>
        </TabsContent>

        {/* Target Prices */}
        <TabsContent value="targets" className="space-y-4">
          <SignalCard
            testId="targets-card"
            title="Kensys Target Prices (Dynamic Upside) → Forward Alpha"
            description="Do high target-price-upside stocks outperform? Monthly rebalance, upside = target / price − 1."
            signal={targets}
          >
            {(res) => (
              <>
                {Object.entries(res.signals || {}).map(([name, block]) => (
                  <SubSignalBlock key={name} name={name} block={block} horizons={res.horizons}
                    capData={res.by_cap_bucket?.[name]} />
                ))}
                {res.target_freshness && (
                  <div className="pt-2">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-sm font-semibold">Target Freshness (3M IC)</h3>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="freshness-grid">
                      {Object.entries(res.target_freshness).map(([bench, f]) => (
                        <div key={bench} className="border rounded-lg p-3">
                          <div className="text-xs text-muted-foreground mb-2">
                            {bench === "spy" ? "vs S&P 500" : "vs Sector"}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              Fresh (≤30d):{" "}
                              <span className="font-mono">{f.fresh_0_30d?.mean_ic ?? "-"}</span>
                              <span className="text-muted-foreground"> (t={f.fresh_0_30d?.nw_tstat ?? "-"})</span>
                            </div>
                            <div>
                              Stale (&gt;60d):{" "}
                              <span className="font-mono">{f.stale_over_60d?.mean_ic ?? "-"}</span>
                              <span className="text-muted-foreground"> (t={f.stale_over_60d?.nw_tstat ?? "-"})</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </SignalCard>
        </TabsContent>

        {/* Nowcasting */}
        <TabsContent value="nowcasting" className="space-y-4">
          <SignalCard
            testId="nowcasting-card"
            title="Nowcasting Regimes → Forward Alpha"
            description="Do Soft-DTW cluster assignments and expected gradients predict alpha? Monthly rebalance."
            signal={nowcasting}
          >
            {(res) => (
              <>
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">Cluster Regimes</h3>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <ClusterSection
                    clusters={res.clusters}
                    clusterMeta={res.cluster_meta}
                    hiConfSmw={res.clusters_high_confidence_smw_63}
                    horizons={res.horizons}
                    testIdPrefix="clusters"
                  />
                  {res.smw_by_cap_bucket_63 && Object.keys(res.smw_by_cap_bucket_63).length > 0 && (
                    <div className="pt-1" data-testid="smw-capsplit">
                      <div className="text-sm font-medium mb-2">
                        Strong − Weak by cap segment{" "}
                        <span className="text-muted-foreground font-normal">(3M)</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {Object.entries(res.smw_by_cap_bucket_63).map(([b, e]) => (
                          <div key={b} className="border rounded-lg p-3 space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{CAP_LABELS[b] || b}</span>
                              <SigBadge t={e.sector?.nw_tstat} />
                            </div>
                            <div className="text-lg font-bold">
                              <AlphaVal value={e.sector?.mean_pct} />
                              <span className="text-xs font-normal text-muted-foreground ml-1">vs sector</span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              t={e.sector?.nw_tstat ?? "-"} · vs SPY:{" "}
                              <AlphaVal value={e.spy?.mean_pct} /> (t={e.spy?.nw_tstat ?? "-"})
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {Object.entries(res.signals || {}).map(([name, block]) => (
                  <SubSignalBlock key={name} name={name} block={block} horizons={res.horizons}
                    capData={res.by_cap_bucket?.[name]} />
                ))}
              </>
            )}
          </SignalCard>
        </TabsContent>

        {/* Options Overlay */}
        <TabsContent value="options">
          <OptionsOverlayTab signal={overlay} />
        </TabsContent>

        {/* Super Signal */}
        <TabsContent value="supersignal">
          <SuperSignalTab signal={superSignal} />
        </TabsContent>

        {/* Big Cap Lab */}
        <TabsContent value="bigcap">
          <BigCapLabTab signal={bigCapLab} />
        </TabsContent>

        {/* Regime Lab */}
        <TabsContent value="regimelab">
          <RegimeLabTab signal={regimeLab} />
        </TabsContent>

        {/* Hidden Gems */}
        <TabsContent value="gems">
          <HiddenGemsTab signal={hiddenGems} />
        </TabsContent>

        {/* White Paper */}
        <TabsContent value="whitepaper">
          <WhitePaperTab token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
